import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {swal, swalConfirm, swalError, swalLoading, swalSuccess} from "../../../helpers/SweetAlertWrapper";
import Page from "../../../components/BaseComponents/Page/Page";
import {FaRegularIcon, FaSolidIcon} from "../../../components/FontAwesomeIcons";
import AppConfig from "../../../config/AppConfig";
import standardEventCover from "../../../img/coriandoli.png";
import {USER_EVENT_THEME_COLORS, USER_EVENT_THEME_FONTS} from "../CreateEvent/CreateEventConstants";
import {UsersModuleEventsAPI} from "../../../api/UsersModuleEvents/UsersModuleEventsAPI";
import ShortLinkAPI from "../../../api/ShortLink/ShortLinkAPI";
import {BookizonAppManager} from "../../../index";
import {UserEventStatistics} from "./UserEventStatistics";
import {UserEventReservationsList} from "./UserEventReservationsList";
import {UserEventReservationDetailPage} from "./UserEventReservationDetailPage";

export class UserEventPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            eventInfo: {},
            eventStatistics: {},
            selectedReservation: null,
            reservationDetailPageOpen: false,
            reservationsListRefreshKey: 0,
            fetching: true,
            deleting: false
        };
        this.coverStyle = {
            minHeight: "260px",
            backgroundSize: "cover",
            backgroundPosition: "center",
            overflow: "hidden",
        };
        this.coverOverlayBaseStyle = {
            minHeight: "260px",
        };
        this.iconBoxStyle = {
            width: "38px",
            minWidth: "38px",
            height: "38px",
        };
        this.colorDotStyle = {
            width: "12px",
            height: "12px",
        };
        this.emptyBoxStyle = {
            minHeight: "220px",
        }
    }

    componentDidMount() {
        this.fetchEvent()
    }

    fetchEvent = _ => {
        this.setState({fetching: true})
        Promise.all([
            UsersModuleEventsAPI.User.getFullUserEventInfo(this.props.eventId),
            UsersModuleEventsAPI.User.getUserEventStatistics(this.props.eventId)
        ])
            .then(data => {
                this.setState({
                    eventInfo: data[0] || {},
                    eventStatistics: data[1] || {},
                    fetching: false
                })
            })
            .catch(error => {
                swalError(error)
                this.setState({fetching: false})
            })
    }

    formatDate = date => {
        const parsedDate = date ? moment(date) : null;
        return parsedDate && parsedDate.isValid() ? parsedDate.format("DD MMMM YYYY") : null;
    }

    formatDateRange = eventInfo => {
        const startDate = this.formatDate(eventInfo.start_date);
        const endDate = this.formatDate(eventInfo.end_date);
        if (startDate && endDate && startDate !== endDate) {
            return `${startDate} - ${endDate}`;
        }
        return startDate || endDate || "Data non impostata";
    }

    getEventStyleConfig = eventInfo => {
        const settings = eventInfo.settings || {};
        const selectedColor = settings.landing_primary_color && settings.landing_primary_color !== "0" ?
            settings.landing_primary_color :
            USER_EVENT_THEME_COLORS[0].value;
        const selectedFontFamily = settings.landing_font_family;
        const selectedFontKey = Object.keys(USER_EVENT_THEME_FONTS).find(key => {
            const font = USER_EVENT_THEME_FONTS[key];
            return font.label === selectedFontFamily || key === selectedFontFamily;
        });
        const selectedFont = USER_EVENT_THEME_FONTS[selectedFontKey] || USER_EVENT_THEME_FONTS.roboto;
        const imageUrl = !eventInfo.image_url ?
            standardEventCover :
            eventInfo.image_url.indexOf("http") === 0 ?
                eventInfo.image_url :
                `${AppConfig.webServerUrl}${eventInfo.image_url.indexOf("/") === 0 ? eventInfo.image_url : `/${eventInfo.image_url}`}`;

        return {
            fontImport: selectedFont.import,
            coverStyle: {
                ...this.coverStyle,
                backgroundImage: `url(${imageUrl})`,
                fontFamily: selectedFont.property.replace(" !important", ""),
            },
            coverOverlayStyle: {
                ...this.coverOverlayBaseStyle,
                background: `linear-gradient(180deg, ${selectedColor}33 0%, ${selectedColor}dd 100%)`,
            }
        };
    }

    renderLoading = _ => (
        <div className={"d-flex align-items-center justify-content-center text-muted"} style={this.emptyBoxStyle}>
            <i className="fa fa-spin fa-spinner"/>
        </div>
    )

    handleShareEventLink = _ => {
        let url = this.state.eventInfo.event_link;
        swalLoading("Attendi, stiamo generando lo scontrino")
        ShortLinkAPI.generate(url)
            .then(data => {
                let msg = "Ciao, clicca qui e partecipa al mio evento " + this.state.eventInfo.name + ": " + data.short_link
                BookizonAppManager.shareData(msg)
                swal.close()
            })
            .catch(swalError)
    }

    handleReservationClick = reservation => {
        this.setState({
            selectedReservation: reservation,
            reservationDetailPageOpen: true
        })
    }

    handleReservationDetailDismiss = _ => {
        this.setState({
            selectedReservation: null,
            reservationDetailPageOpen: false
        })
    }

    handleReservationStatusChange = reservation => {
        this.setState({
            selectedReservation: reservation,
            reservationsListRefreshKey: this.state.reservationsListRefreshKey + 1
        })
    }

    handleDeleteEvent = _ => {
        swalConfirm(
            "Sei sicuro di volerlo fare? Tutti i partecipanti verranno informati che l'evento è stato annullato",
            "Elimina",
            "Annulla"
        )
            .then(_ => {
                this.setState({deleting: true});
                swalLoading("Eliminazione evento in corso...");
                UsersModuleEventsAPI.User.delete(this.props.eventId)
                    .then(response => {
                        swal.safeClose();
                        swalSuccess(response.message || "Evento eliminato correttamente.").then(_ => {
                            this.setState({deleting: false});
                            this.props.onDismiss();
                        });
                    })
                    .catch(error => {
                        swal.safeClose();
                        swalError(error);
                        this.setState({deleting: false});
                    });
            })
            .catch(_ => null);
    }

    render() {
        const eventInfo = this.state.eventInfo || {};
        const styleConfig = this.getEventStyleConfig(eventInfo);
        const eventName = eventInfo.name || eventInfo.title || "Evento";
        const dateRange = this.formatDateRange(eventInfo);

        return (
            <React.Fragment>
                <Page
                    title={eventName}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={true}
                >
                    {
                        this.state.fetching ?
                            this.renderLoading() :
                            <div className={"container py-3"}>
                                <style>
                                    {styleConfig.fontImport}
                                </style>

                                <div className={"card border-0 shadow-sm overflow-hidden mb-3"}>
                                    <div style={styleConfig.coverStyle}>
                                        <div className={"d-flex flex-column justify-content-end text-white p-3"}
                                             style={styleConfig.coverOverlayStyle}>
                                            <h4 className={"font-weight-bold mb-2"}>{eventName}</h4>
                                            <div className={"small"}>
                                                <FaRegularIcon name={"calendar"} className={"mr-1"}/>
                                                {dateRange}
                                                {
                                                    eventInfo.location_name &&
                                                    <span className={"ml-2"}>
                                                    <FaSolidIcon name={"map-marker-alt"} className={"mr-1"}/>
                                                        {eventInfo.location_name}
                                                </span>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={"d-flex mb-3 align-items-center justify-content-around"}>
                                    <button
                                        className={"btn btn-primary p-2  w-50 ml-1"}
                                        onClick={this.handleShareEventLink}>Condividi <FaSolidIcon name={"share"}/>
                                    </button>
                                    <a
                                        className={"btn btn-info p-2  w-50 ml-1"}
                                        href={this.state.eventInfo.event_link}>Vedi pagina <FaSolidIcon name={"eye"}/>
                                    </a>
                                </div>

                                <UserEventStatistics statistics={this.state.eventStatistics}/>

                                <UserEventReservationsList
                                    key={this.state.reservationsListRefreshKey}
                                    eventId={this.props.eventId}
                                    onReservationClick={this.handleReservationClick}
                                />

                                <div className={"text-center my-4"}>
                                    <button
                                        type={"button"}
                                        className={"btn btn-sm btn-link text-danger "}
                                        onClick={this.handleDeleteEvent}
                                        disabled={this.state.deleting}
                                    >
                                        <FaSolidIcon name={"trash"}/> Elimina evento
                                    </button>
                                </div>
                            </div>
                    }
                </Page>

                {
                    this.state.reservationDetailPageOpen &&
                    <UserEventReservationDetailPage
                        reservation={this.state.selectedReservation}
                        onReservationStatusChange={this.handleReservationStatusChange}
                        onDismiss={this.handleReservationDetailDismiss}
                    />
                }
            </React.Fragment>
        );
    }
}

UserEventPage.propTypes = {
    onDismiss: PropTypes.func,
    eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    eventType: PropTypes.string
};
